/**
 * @file pwa/task-pack.ts
 * @description 任务离线包预下载 orchestrator。
 *
 * 本阶段仍统一使用 IndexedDB：
 * - API JSON → cache_api / cache_tasks / cache_cases / cache_forms
 * - 二进制文件 → cache_media Blob
 * - 资源完整度 → cache_task_resources
 */
import { collectFileIds, type FormSchema } from '@zhcb/forms/browser';
import { apiClient } from '@/lib/api-client';
import { getOfflineDb, type CachedTaskPack, type CachedTaskResource } from '@/pwa/db';
import { putCachedApi } from '@/pwa/offline-api-cache';
import { makeTaskResourceId, putTaskPack, putTaskResource } from '@/pwa/task-pack-manifest';

/** 预下载停止阈值：剩余空间 < 50MB 时停 */
const QUOTA_FLOOR_BYTES = 50 * 1024 * 1024;

/** 二进制文件并发下载上限 */
const BINARY_DOWNLOAD_CONCURRENCY = 5;

/** 表单实例并发处理上限 */
const FORM_INSTANCE_CONCURRENCY = 4;

/** 简易并发池：N 个 worker 各自从共享 next() 取任务，直到取完。 */
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let next = 0;
  const runOne = async (): Promise<void> => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      await worker(items[i]!, i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runOne));
}

const SCOPE_FORM_DOCX = 'form-template-docx';
const SCOPE_ATTACHMENT = 'form-attachment';
const SCOPE_CASE_DOCUMENT = 'case-document';

type BinaryScope = typeof SCOPE_FORM_DOCX | typeof SCOPE_ATTACHMENT | typeof SCOPE_CASE_DOCUMENT;

/** prefetchTaskFull 进度回调 —— 统计二进制下载 */
export interface PrefetchProgress {
  downloaded: number;
  total: number;
  currentFileId?: number;
  currentScope?: BinaryScope;
}

export interface PrefetchResult {
  /** 兼容旧 UI：已缓存表单 schema 数 */
  forms: number;
  task: { cached: boolean };
  caseDetail: { cached: boolean };
  clientProfile: { total: number; cached: number };
  fieldData: { total: number; cached: number };
  /** missing：file 在 server 端被删除或 meta 拉取失败导致整批未缓存 */
  docxFiles: { total: number; downloaded: number; skipped: number; missing: number };
  caseDocuments: { total: number; downloaded: number; skipped: number; missing: number };
  attachments: { total: number; downloaded: number; skipped: number; missing: number };
  stoppedAtQuota: boolean;
  errors: string[];
}

/** BinaryScope → cache_task_resources.resourceType 映射 */
const BINARY_SCOPE_TO_RESOURCE_TYPE: Record<
  BinaryScope,
  'form-template-docx' | 'case-document' | 'form-attachment'
> = {
  [SCOPE_FORM_DOCX]: 'form-template-docx',
  [SCOPE_CASE_DOCUMENT]: 'case-document',
  [SCOPE_ATTACHMENT]: 'form-attachment',
};

interface TaskDetailResponse {
  task: {
    id: number;
    caseId?: number;
    caseCode: string | null;
    clientId: number | null;
    clientName: string | null;
    forms: Array<{ id: number; templateId: number; status: string }>;
  };
}

interface CaseDetailResponse {
  item: {
    id: number;
    caseCode: string;
    clientId: number | null;
    clientName: string | null;
    documents: Array<{
      id: number;
      docType: string;
      fileId: number;
      filename: string | null;
      sizeBytes: number | null;
      mimeType: string | null;
    }>;
  };
}

interface FormInstanceResponse {
  instance: {
    id: number;
    templateId: number;
    templateVersionId: number;
    data: Record<string, unknown>;
  };
}

interface FormTemplateResponse {
  template: { id: number; code: string; name: string };
  versions: Array<{
    id: number;
    templateId: number;
    version: number;
    schemaJson: FormSchema;
    docxFileId: number | null;
  }>;
}

interface MetaBatchResponse {
  items: Array<{
    id: number;
    filename: string;
    sizeBytes: number;
    mimeType: string;
    deleted: boolean;
  }>;
}

interface DownloadUrlResponse {
  url: string;
}

interface DownloadItem {
  fileId: number;
  scope: BinaryScope;
  ownerType: 'form_template_version' | 'form_instance' | 'case';
  ownerId: number;
}

async function hasEnoughQuota(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return true;
  try {
    const est = await navigator.storage.estimate();
    if (typeof est.quota !== 'number' || typeof est.usage !== 'number') return true;
    return est.quota - est.usage > QUOTA_FLOOR_BYTES;
  } catch (err) {
    console.error('[task-pack] navigator.storage.estimate failed', err);
    return true;
  }
}

function emptyResult(errors: string[] = []): PrefetchResult {
  return {
    forms: 0,
    task: { cached: false },
    caseDetail: { cached: false },
    clientProfile: { total: 0, cached: 0 },
    fieldData: { total: 0, cached: 0 },
    docxFiles: { total: 0, downloaded: 0, skipped: 0, missing: 0 },
    caseDocuments: { total: 0, downloaded: 0, skipped: 0, missing: 0 },
    attachments: { total: 0, downloaded: 0, skipped: 0, missing: 0 },
    stoppedAtQuota: false,
    errors,
  };
}

async function cacheApiResource(
  taskId: number,
  url: string,
  scope: string,
  resourceType: CachedTaskResource['resourceType'],
  resourceId: string | number,
  errors: string[],
): Promise<boolean> {
  try {
    const data = await apiClient.get<unknown>(url);
    await putCachedApi(url, data, { taskId, scope });
    await putTaskResource({
      id: makeTaskResourceId(taskId, resourceType, resourceId),
      taskId,
      resourceType,
      resourceId: String(resourceId),
      status: 'cached',
      cachedAt: Date.now(),
    });
    return true;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    errors.push(`${url}: ${msg}`);
    await putTaskResource({
      id: makeTaskResourceId(taskId, resourceType, resourceId),
      taskId,
      resourceType,
      resourceId: String(resourceId),
      status: 'error',
      errorMessage: msg,
      cachedAt: Date.now(),
    });
    return false;
  }
}

interface LoadFileMetasResult {
  /** 成功取到 meta 的 fileId → 元数据 */
  metas: Map<number, { filename: string; mimeType: string; sizeBytes: number }>;
  /** server 端返回 deleted:true 的 fileId 集合 */
  deletedFileIds: Set<number>;
  /** meta-batch 整批失败导致 meta 缺失的 fileId 集合 */
  chunkFailedFileIds: Set<number>;
}

async function loadFileMetas(
  items: DownloadItem[],
  errors: string[],
): Promise<LoadFileMetasResult> {
  const metas = new Map<number, { filename: string; mimeType: string; sizeBytes: number }>();
  const deletedFileIds = new Set<number>();
  const chunkFailedFileIds = new Set<number>();
  const ids = items.map((it) => it.fileId);

  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    if (chunk.length === 0) continue;
    try {
      const metaRes = await apiClient.post<MetaBatchResponse>('/api/files/meta-batch', {
        ids: chunk,
      });
      for (const it of metaRes.items) {
        if (it.deleted) {
          deletedFileIds.add(it.id);
        } else {
          metas.set(it.id, {
            filename: it.filename,
            mimeType: it.mimeType,
            sizeBytes: it.sizeBytes,
          });
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`meta-batch ${i}-${i + chunk.length}: ${msg}`);
      for (const id of chunk) chunkFailedFileIds.add(id);
    }
  }

  return { metas, deletedFileIds, chunkFailedFileIds };
}

export async function prefetchTaskFull(
  taskId: number,
  onProgress?: (p: PrefetchProgress) => void,
): Promise<PrefetchResult> {
  const errors: string[] = [];
  const result = emptyResult(errors);
  const db = await getOfflineDb();

  let taskRes: TaskDetailResponse;
  try {
    taskRes = await apiClient.get<TaskDetailResponse>(`/api/audit-tasks/${taskId}`);
    await putCachedApi(`/api/audit-tasks/${taskId}`, taskRes, {
      taskId,
      scope: 'task-detail',
    });
    result.task.cached = true;
    await putTaskResource({
      id: makeTaskResourceId(taskId, 'task', taskId),
      taskId,
      resourceType: 'task',
      resourceId: String(taskId),
      status: 'cached',
      cachedAt: Date.now(),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    errors.push(`task ${taskId}: ${msg}`);
    return result;
  }

  const task = taskRes.task;
  const formInstanceIds = task.forms.map((f) => f.id);
  const downloadByFileId = new Map<number, DownloadItem>();

  let caseRes: CaseDetailResponse | null = null;
  if (typeof task.caseId === 'number') {
    try {
      caseRes = await apiClient.get<CaseDetailResponse>(`/api/cases/${task.caseId}`);
      await putCachedApi(`/api/cases/${task.caseId}`, caseRes, {
        taskId,
        scope: 'case-detail',
      });
      await db.put('cache_cases', {
        caseId: task.caseId,
        caseCode: caseRes.item.caseCode,
        data: caseRes,
        cachedAt: Date.now(),
      });
      result.caseDetail.cached = true;
      await putTaskResource({
        id: makeTaskResourceId(taskId, 'case', task.caseId),
        taskId,
        resourceType: 'case',
        resourceId: String(task.caseId),
        status: 'cached',
        cachedAt: Date.now(),
      });

      for (const doc of caseRes.item.documents) {
        if (doc.fileId > 0 && !downloadByFileId.has(doc.fileId)) {
          downloadByFileId.set(doc.fileId, {
            fileId: doc.fileId,
            scope: SCOPE_CASE_DOCUMENT,
            ownerType: 'case',
            ownerId: task.caseId,
          });
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`case ${task.caseId}: ${msg}`);
    }
  }

  const clientId = task.clientId ?? caseRes?.item.clientId ?? null;
  const clientUrls =
    typeof clientId === 'number'
      ? [
          { url: `/api/clients/${clientId}`, type: 'client' as const, id: clientId },
          { url: `/api/clients/${clientId}/lands`, type: 'client-land' as const, id: clientId },
          {
            url: `/api/clients/${clientId}/processing-sites`,
            type: 'client-processing-site' as const,
            id: clientId,
          },
          { url: `/api/clients/${clientId}/cases`, type: 'client-case' as const, id: clientId },
        ]
      : [];
  result.clientProfile.total = clientUrls.length;
  // 4 个 url 完全独立，并行掉 ~3 RTT 的等待
  const clientResults = await Promise.all(
    clientUrls.map((item) =>
      cacheApiResource(taskId, item.url, 'client-profile', item.type, item.id, errors),
    ),
  );
  result.clientProfile.cached = clientResults.filter(Boolean).length;

  const fieldUrls =
    typeof task.caseId === 'number'
      ? [
          { url: `/api/cases/${task.caseId}/findings`, type: 'finding' as const, id: task.caseId },
          { url: `/api/cases/${task.caseId}/samples`, type: 'sample' as const, id: task.caseId },
          {
            url: '/api/settings/laboratories?pageSize=500&activeOnly=true',
            type: 'laboratory-option' as const,
            id: 'active',
          },
        ]
      : [];
  result.fieldData.total = fieldUrls.length;
  const fieldResults = await Promise.all(
    fieldUrls.map((item) =>
      cacheApiResource(taskId, item.url, 'field-data', item.type, item.id, errors),
    ),
  );
  result.fieldData.cached = fieldResults.filter(Boolean).length;

  // 同 templateId 的 instance 共享同一份 template fetch（同 SOP 多份表单的常见情况）；
  // 表单实例之间互不依赖，bounded concurrency 跑。
  const templatePromises = new Map<number, Promise<FormTemplateResponse>>();
  const fetchTemplate = (templateId: number): Promise<FormTemplateResponse> => {
    let p = templatePromises.get(templateId);
    if (!p) {
      p = apiClient
        .get<FormTemplateResponse>(`/api/form-templates/${templateId}`)
        .then(async (tpl) => {
          await putCachedApi(`/api/form-templates/${templateId}`, tpl, {
            taskId,
            scope: 'form-template',
          });
          await putTaskResource({
            id: makeTaskResourceId(taskId, 'form-template', templateId),
            taskId,
            resourceType: 'form-template',
            resourceId: String(templateId),
            status: 'cached',
            cachedAt: Date.now(),
          });
          return tpl;
        });
      templatePromises.set(templateId, p);
    }
    return p;
  };

  await runWithConcurrency(formInstanceIds, FORM_INSTANCE_CONCURRENCY, async (instanceId) => {
    try {
      const instRes = await apiClient.get<FormInstanceResponse>(
        `/api/form-instances/${instanceId}`,
      );
      await putCachedApi(`/api/form-instances/${instanceId}`, instRes, {
        taskId,
        scope: 'form-instance',
      });
      await putTaskResource({
        id: makeTaskResourceId(taskId, 'form-instance', instanceId),
        taskId,
        resourceType: 'form-instance',
        resourceId: String(instanceId),
        status: 'cached',
        cachedAt: Date.now(),
      });

      const tplRes = await fetchTemplate(instRes.instance.templateId);

      const version = tplRes.versions.find((v) => v.id === instRes.instance.templateVersionId);
      if (!version) return;

      await db.put('cache_forms', {
        templateVersionId: version.id,
        schemaJson: version.schemaJson,
        docxFileId: version.docxFileId,
        cachedAt: Date.now(),
      });
      result.forms++;

      if (typeof version.docxFileId === 'number' && version.docxFileId > 0) {
        if (!downloadByFileId.has(version.docxFileId)) {
          downloadByFileId.set(version.docxFileId, {
            fileId: version.docxFileId,
            scope: SCOPE_FORM_DOCX,
            ownerType: 'form_template_version',
            ownerId: version.id,
          });
        }
      }

      const attachIds = collectFileIds(version.schemaJson, instRes.instance.data).filter(
        (id) => id > 0,
      );
      for (const id of attachIds) {
        if (!downloadByFileId.has(id)) {
          downloadByFileId.set(id, {
            fileId: id,
            scope: SCOPE_ATTACHMENT,
            ownerType: 'form_instance',
            ownerId: instanceId,
          });
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`form ${instanceId}: ${msg}`);
    }
  });

  const items = Array.from(downloadByFileId.values());
  result.docxFiles.total = items.filter((it) => it.scope === SCOPE_FORM_DOCX).length;
  result.caseDocuments.total = items.filter((it) => it.scope === SCOPE_CASE_DOCUMENT).length;
  result.attachments.total = items.filter((it) => it.scope === SCOPE_ATTACHMENT).length;

  const { metas, deletedFileIds, chunkFailedFileIds } = await loadFileMetas(items, errors);
  let binaryDone = 0;
  const reportProgress = (currentFileId?: number, currentScope?: BinaryScope) => {
    onProgress?.({
      downloaded: binaryDone,
      total: items.length,
      currentFileId,
      currentScope,
    });
  };

  const ordered = [
    ...items.filter((it) => it.scope === SCOPE_FORM_DOCX),
    ...items.filter((it) => it.scope === SCOPE_CASE_DOCUMENT),
    ...items.filter((it) => it.scope === SCOPE_ATTACHMENT),
  ];

  let mediaSizeBytes = 0;
  // 共享 stoppedAtQuota 标志：任一 worker 命中阈值都让其他 worker 提前退出
  await runWithConcurrency(ordered, BINARY_DOWNLOAD_CONCURRENCY, async (item) => {
    if (result.stoppedAtQuota) return;
    const { fileId, scope, ownerType, ownerId } = item;
    const resourceType = BINARY_SCOPE_TO_RESOURCE_TYPE[scope];

    const existing = await db.get('cache_media', `file:${fileId}`);
    if (existing) {
      incrementBinary(result, scope, 'skipped');
      binaryDone++;
      reportProgress(fileId, scope);
      mediaSizeBytes += existing.sizeBytes ?? 0;
      return;
    }

    if (!(await hasEnoughQuota())) {
      result.stoppedAtQuota = true;
      return;
    }

    const meta = metas.get(fileId);
    if (!meta) {
      // meta 缺失：必须显式记账，否则进度卡住且 OfflineManagementPage 看不到丢失项
      const reason = deletedFileIds.has(fileId)
        ? 'file deleted server-side'
        : chunkFailedFileIds.has(fileId)
          ? 'meta-batch failed'
          : 'meta missing';
      errors.push(`file ${fileId} (${scope}): ${reason}`);
      await putTaskResource({
        id: makeTaskResourceId(taskId, resourceType, fileId),
        taskId,
        resourceType,
        resourceId: String(fileId),
        status: 'missing',
        errorMessage: reason,
        cachedAt: Date.now(),
      });
      incrementBinary(result, scope, 'missing');
      binaryDone++;
      reportProgress(fileId, scope);
      return;
    }

    try {
      const dl = await apiClient.get<DownloadUrlResponse>(
        `/api/files/${fileId}/download?disposition=attachment`,
      );
      const res = await fetch(dl.url);
      if (!res.ok) throw new Error(`OSS GET ${res.status}`);
      const blob = await res.blob();

      await db.put('cache_media', {
        id: `file:${fileId}`,
        blob,
        filename: meta.filename,
        mimeType: meta.mimeType,
        sizeBytes: meta.sizeBytes,
        scope,
        ownerType,
        ownerId,
        status: 'cached',
        uploadedFileId: fileId,
        retryCount: 0,
        createdAt: Date.now(),
      });
      await putTaskResource({
        id: makeTaskResourceId(taskId, resourceType, fileId),
        taskId,
        resourceType,
        resourceId: String(fileId),
        status: 'cached',
        sizeBytes: meta.sizeBytes,
        cachedAt: Date.now(),
      });
      incrementBinary(result, scope, 'downloaded');
      binaryDone++;
      mediaSizeBytes += meta.sizeBytes;
      reportProgress(fileId, scope);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`file ${fileId} (${scope}): ${msg}`);
      await putTaskResource({
        id: makeTaskResourceId(taskId, resourceType, fileId),
        taskId,
        resourceType,
        resourceId: String(fileId),
        status: 'error',
        errorMessage: msg,
        cachedAt: Date.now(),
      });
    }
  });

  const pack: CachedTaskPack = {
    taskId,
    caseId: task.caseId ?? caseRes?.item.id ?? 0,
    clientId,
    caseCode: task.caseCode ?? caseRes?.item.caseCode ?? null,
    clientName: task.clientName ?? caseRes?.item.clientName ?? null,
    status: result.stoppedAtQuota ? 'quota_limited' : errors.length > 0 ? 'partial' : 'ready',
    data: taskRes,
    downloadedAt: Date.now(),
    updatedAt: Date.now(),
    sizeBytes: mediaSizeBytes,
    formsCount: result.forms,
    mediaCount:
      result.docxFiles.downloaded +
      result.docxFiles.skipped +
      result.docxFiles.missing +
      result.caseDocuments.downloaded +
      result.caseDocuments.skipped +
      result.caseDocuments.missing +
      result.attachments.downloaded +
      result.attachments.skipped +
      result.attachments.missing,
    apiCount:
      Number(result.task.cached) +
      Number(result.caseDetail.cached) +
      result.clientProfile.cached +
      result.fieldData.cached +
      result.forms * 2,
    errorCount: errors.length,
  };
  await putTaskPack(pack);

  return result;
}

function incrementBinary(
  result: PrefetchResult,
  scope: BinaryScope,
  kind: 'downloaded' | 'skipped' | 'missing',
): void {
  if (scope === SCOPE_FORM_DOCX) result.docxFiles[kind]++;
  else if (scope === SCOPE_CASE_DOCUMENT) result.caseDocuments[kind]++;
  else result.attachments[kind]++;
}
